import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar as CalIcon, Sparkles, X } from '../components/Icons.jsx';

const STORAGE_KEY = 'mm-calendar-v1';
const KEY_DATES = [
  { date: '2026-10-15', label: 'UCAS deadline (Medicine, Dentistry)', kind: 'deadline' },
  { date: '2026-09-01', label: 'UCAT booking opens', kind: 'milestone' },
  { date: '2026-08-12', label: 'Career Camp · London cohort', kind: 'milestone' }
];

// Colour coding for every event kind (calendar pills + legend share this).
const KIND_STYLE = {
  deadline: { bg: '#fee2e2', fg: '#b91c1c', label: 'Deadline' },
  milestone: { bg: '#fef3c7', fg: '#b45309', label: 'Milestone' },
  plan: { bg: '#e7f7ef', fg: 'var(--live-green)', label: 'Drill / study' },
  mock: { bg: '#ede9fe', fg: '#6d28d9', label: 'Mock exam' },
  review: { bg: '#e0f2fe', fg: '#0369a1', label: 'Review' },
  rest: { bg: '#f1f5f9', fg: '#64748b', label: 'Rest' },
  user: { bg: 'var(--accent-soft)', fg: 'var(--brand-deep)', label: 'Your event' },
};
const kindStyle = (kind) => KIND_STYLE[kind] || KIND_STYLE.user;

function dateKey(d) { return d.toISOString().slice(0, 10); }
function loadEvents() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function saveEvents(events) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(events)); } catch {}
}

// ── UCAT plan generator ──────────────────────────────────────────────────
// A phased, progressive plan instead of the same three drills every week:
//   Foundation → learn each section's method (lightly timed)
//   Build      → timed drills across ALL four sections + weekly mini-mocks
//   Polish     → full timed mocks + targeted review of weak areas
//   Final week → taper: light review, error-log, logistics, rest before exam
// Intensity scales how many sessions land each week. Days are 0=Mon … 6=Sun.
const SECTIONS = {
  VR: 'Verbal Reasoning',
  DM: 'Decision Making',
  QR: 'Quantitative Reasoning',
  SJT: 'Situational Judgement',
};

const PHASE_LABEL = {
  foundation: '📚 Phase 1 · Foundations — learn each section’s method',
  build: '⏱️ Phase 2 · Build — timed drills across all sections',
  polish: '🎯 Phase 3 · Polish — full mocks + targeted review',
};

function foundationWeek(w, intense) {
  // Rotate which two sections you "learn" each week so all four get covered.
  const pairs = [
    [['VR', 'skim-and-scan, keyword location, “can’t tell” vs true/false'],
     ['DM', 'syllogisms, logic puzzles & recognising arguments']],
    [['QR', 'percentages, ratios, speed-distance + the on-screen calculator'],
     ['SJT', 'GMC Good Medical Practice, then an intro appropriateness set']],
  ][w % 2];
  const items = [
    { day: 0, label: `${SECTIONS[pairs[0][0]]} · learn the method — ${pairs[0][1]} (untimed)`, kind: 'plan' },
    { day: 1, label: 'Daily Question — 10-min warm-up', kind: 'plan' },
    { day: 2, label: `${SECTIONS[pairs[1][0]]} · learn the method — ${pairs[1][1]} (untimed)`, kind: 'plan' },
    { day: 4, label: w % 2 === 0 ? 'VR · speed-reading drill — build your pace' : 'QR · calculator keypad-accuracy drill', kind: 'plan' },
    { day: 6, label: 'Rest day — let it consolidate', kind: 'rest' },
  ];
  if (intense) {
    items.push({ day: 3, label: 'QR · mental-maths & keypad speed (10 min daily habit)', kind: 'plan' });
    items.push({ day: 5, label: 'SJT · scenario set — name the GMC duty behind each answer', kind: 'plan' });
  }
  return items;
}

function buildWeek(w, weeks, intense, foundationEnd) {
  const intoBuild = w - foundationEnd; // 0-indexed within the build phase
  const items = [
    { day: 0, label: 'VR · timed set — ~2 min/passage, then log every miss', kind: 'plan' },
    { day: 1, label: 'DM · timed set — Venn, probability trees & syllogisms', kind: 'plan' },
    { day: 3, label: 'QR · timed set — data interpretation + keypad accuracy', kind: 'plan' },
    { day: 4, label: 'SJT · timed appropriateness & importance set', kind: 'plan' },
    { day: 5, label: intoBuild >= 1
        ? 'Mini-mock — 2 sections back-to-back under exam timing'
        : 'Mini-mock — 1 section under strict exam timing', kind: 'mock' },
    { day: 6, label: 'Review — redo every wrong answer, update your error log', kind: 'review' },
  ];
  if (intense) items.push({ day: 2, label: 'Targeted drill — your current weakest topic', kind: 'plan' });
  return items;
}

function polishWeek(intense) {
  const items = [
    { day: 0, label: 'Weakest section · high-difficulty timed drill', kind: 'plan' },
    { day: 1, label: 'SJT · full timed section + review every rationale', kind: 'plan' },
    { day: 3, label: 'Full mock — all sections, real exam interface (≈2 hrs)', kind: 'mock' },
    { day: 4, label: 'Mock review — analyse every error, spot the patterns', kind: 'review' },
    { day: 5, label: 'Timed mixed set — keep all four sections sharp', kind: 'plan' },
    { day: 6, label: 'Rest day — recover before the next push', kind: 'rest' },
  ];
  if (intense) items.push({ day: 2, label: 'Speed & accuracy drill on a recurring weak topic', kind: 'plan' });
  return items;
}

function taperWeek() {
  return [
    { day: 0, label: 'Light timed set — stay warm, no new material', kind: 'plan' },
    { day: 1, label: 'Review your error log + section strategies', kind: 'review' },
    { day: 2, label: 'SJT · skim GMC duties + one short set', kind: 'plan' },
    { day: 3, label: 'Short mixed warm-up (30 min) — then stop', kind: 'plan' },
    { day: 4, label: 'Rest — sort ID, test centre, route & an early night', kind: 'rest' },
  ];
}

function suggestUcatPlan({ start, weeks, examDate = null, intensity = 'balanced' }) {
  const events = [];
  const monday0 = new Date(start);
  const intense = intensity === 'intensive';
  const examKey = examDate ? dateKey(examDate) : null;

  const foundationEnd = Math.max(1, Math.round(weeks * 0.3));
  const buildEnd = Math.max(foundationEnd + 1, Math.round(weeks * 0.7));
  const phaseOf = (w) => (w < foundationEnd ? 'foundation' : w < buildEnd ? 'build' : 'polish');

  const place = (w, day, label, kind, tag) => {
    const d = new Date(monday0);
    d.setDate(monday0.getDate() + w * 7 + day);
    // Never schedule work on or after exam day — the day itself just gets the
    // exam marker, and anything past it is dropped.
    if (examKey && dateKey(d) >= examKey) return;
    events.push({ id: `ucat-w${w}-d${day}-${tag}`, date: dateKey(d), label, kind });
  };

  let lastPhase = null;
  for (let w = 0; w < weeks; w++) {
    const isFinal = weeks > 1 && w === weeks - 1;
    const phase = phaseOf(w);
    if (!isFinal && phase !== lastPhase) {
      place(w, 0, PHASE_LABEL[phase], 'milestone', 'phase');
      lastPhase = phase;
    }
    const items = isFinal
      ? taperWeek()
      : phase === 'foundation'
      ? foundationWeek(w, intense)
      : phase === 'build'
      ? buildWeek(w, weeks, intense, foundationEnd)
      : polishWeek(intense);
    items.forEach((it, i) => place(w, it.day, it.label, it.kind, `${it.kind}${i}`));
  }

  if (examDate) {
    events.push({ id: 'ucat-exam-day', date: examKey, label: '🎯 UCAT exam day — you’ve got this', kind: 'milestone' });
  }
  return events;
}

export default function Calendar() {
  const [now, setNow] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [events, setEvents] = useState(() => loadEvents());
  const [adding, setAdding] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);

  const monthLabel = now.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  const monthGrid = useMemo(() => buildMonthGrid(now), [now]);

  const eventsByDate = useMemo(() => {
    const all = [...KEY_DATES, ...events];
    const map = {};
    for (const e of all) (map[e.date] = map[e.date] || []).push(e);
    return map;
  }, [events]);

  const addEvent = (date, label) => {
    const next = [...events, { id: Date.now().toString(36), date: dateKey(date), label, kind: 'user' }];
    setEvents(next); saveEvents(next); setAdding(null);
  };
  const removeEvent = (id) => {
    const next = events.filter((e) => e.id !== id);
    setEvents(next); saveEvents(next);
  };
  const addUcatPlan = ({ weeks, examDate = null, intensity = 'balanced' }) => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    // Start next Monday (or today if it's already Monday).
    start.setDate(start.getDate() + ((1 + 7 - start.getDay()) % 7));
    const plan = suggestUcatPlan({ start, weeks, examDate, intensity });
    const next = [...events.filter((e) => !e.id.startsWith('ucat-')), ...plan];
    setEvents(next); saveEvents(next); setPlanOpen(false);
    // Jump the view to the plan's first month so the user actually sees it.
    const firstMonth = new Date(start); firstMonth.setDate(1);
    setNow(firstMonth);
  };
  const clearAll = () => { setEvents([]); saveEvents([]); };

  const downloadIcs = () => {
    const all = [...KEY_DATES, ...events];
    const ics = buildIcs(all);
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'medi-ucat-calendar.ics';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <div className="page-head">
        <Link to="/apply" className="back-link"><ArrowLeft width="16" height="16" />Back to Apply</Link>
        <h1 style={{ marginTop: 12 }}>Your calendar</h1>
        <p className="subtitle">Plan your own diary. UCAS deadlines pre-loaded. Import a UCAT prep plan in one click — or download the whole calendar as .ics for Apple Calendar, Google Calendar or Outlook.</p>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setNow(addMonths(now, -1))} style={navBtn}>‹</button>
            <h2 style={{ margin: 0, fontSize: 18, minWidth: 180, textAlign: 'center' }}>{monthLabel}</h2>
            <button onClick={() => setNow(addMonths(now, 1))} style={navBtn}>›</button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setPlanOpen(true)} className="btn btn-primary"><Sparkles width="14" height="14" />Suggest UCAT plan</button>
            <button onClick={downloadIcs} className="btn btn-secondary">⤓ Download .ics</button>
            <button onClick={clearAll} className="btn btn-secondary">Clear my events</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div key={d} style={{ padding: '4px 6px' }}>{d}</div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {monthGrid.map((day) => {
            const k = dateKey(day.date);
            const inMonth = day.date.getMonth() === now.getMonth();
            const isToday = k === dateKey(new Date());
            const cellEvents = eventsByDate[k] || [];
            return (
              <button
                key={k + day.date.toISOString()}
                onClick={() => setAdding(day.date)}
                style={{
                  textAlign: 'left', padding: '6px 8px',
                  minHeight: 78, cursor: 'pointer',
                  background: isToday ? 'var(--accent-soft)' : inMonth ? '#fff' : 'var(--card-soft)',
                  border: `1px solid ${isToday ? 'var(--brand-purple)' : 'var(--border)'}`,
                  borderRadius: 8, opacity: inMonth ? 1 : 0.55,
                  fontFamily: 'inherit', fontSize: 12,
                  display: 'flex', flexDirection: 'column', gap: 4,
                  overflow: 'hidden'
                }}
              >
                <div style={{ fontWeight: 700, color: isToday ? 'var(--brand-purple)' : 'var(--text-2)' }}>{day.date.getDate()}</div>
                {cellEvents.slice(0, 3).map((e) => {
                  const st = kindStyle(e.kind);
                  return (
                    <span key={e.id || e.label} title={e.label} style={{
                      padding: '2px 6px', borderRadius: 4,
                      background: st.bg,
                      color: st.fg,
                      fontSize: 11, fontWeight: 600,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>{e.label}</span>
                  );
                })}
                {cellEvents.length > 3 && (
                  <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>+{cellEvents.length - 3} more</span>
                )}
              </button>
            );
          })}
        </div>

        <Legend />
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Coming up · next 5 events</h3>
        <UpcomingList eventsByDate={eventsByDate} onRemove={removeEvent} />
      </div>

      {adding && <AddEventModal date={adding} onClose={() => setAdding(null)} onSave={(label) => addEvent(adding, label)} />}
      {planOpen && <PlanModal onClose={() => setPlanOpen(false)} onApply={addUcatPlan} />}
    </>
  );
}

function Legend() {
  const keys = ['plan', 'mock', 'review', 'rest', 'milestone', 'deadline'];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      {keys.map((k) => {
        const st = kindStyle(k);
        return (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: st.bg, border: `1px solid ${st.fg}33` }} />
            {st.label}
          </span>
        );
      })}
    </div>
  );
}

function UpcomingList({ eventsByDate, onRemove }) {
  const today = dateKey(new Date());
  const flat = Object.entries(eventsByDate)
    .filter(([d]) => d >= today)
    .flatMap(([d, list]) => list.map((e) => ({ ...e, date: d })))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);
  if (flat.length === 0) return <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>Nothing scheduled. Tap a day to add an event, or import a UCAT plan.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {flat.map((e) => (
        <div key={e.id || e.label + e.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: 'var(--card-soft)' }}>
          <div>
            <strong style={{ fontSize: 14 }}>{e.label}</strong>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{new Date(e.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
          {e.id && (
            <button onClick={() => onRemove(e.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} aria-label="Remove"><X width="14" height="14" /></button>
          )}
        </div>
      ))}
    </div>
  );
}

function AddEventModal({ date, onClose, onSave }) {
  const [text, setText] = useState('');
  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Add event</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          {date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        <input
          autoFocus value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) onSave(text.trim()); }}
          placeholder="e.g. UCAT mock 1"
          style={{ width: '100%', padding: '12px 14px', fontSize: 15, border: '1px solid var(--border-strong)', borderRadius: 10, marginBottom: 14 }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button onClick={() => text.trim() && onSave(text.trim())} disabled={!text.trim()} className="btn btn-primary" style={{ opacity: text.trim() ? 1 : 0.5 }}>Add</button>
        </div>
      </div>
    </div>
  );
}

function nextMonday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7));
  return d;
}

function PlanModal({ onClose, onApply }) {
  const [intensity, setIntensity] = useState('balanced');
  const [examDate, setExamDate] = useState('');

  const minDate = dateKey(new Date(Date.now() + 14 * 864e5)); // at least ~2 weeks out
  const weeksUntilExam = examDate
    ? Math.max(2, Math.ceil((new Date(examDate) - nextMonday()) / (7 * 864e5)))
    : null;
  const examValid = weeksUntilExam != null && weeksUntilExam >= 2;

  const intensities = [
    { v: 'balanced', label: 'Balanced', note: '4–5 sessions/week — fits around A-levels' },
    { v: 'intensive', label: 'Intensive', note: '6 sessions/week — holidays or full focus' },
  ];

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Build your UCAT plan</h3>
        <p style={{ margin: '0 0 16px', color: 'var(--text-muted)', fontSize: 13 }}>
          A phased plan — <strong>Foundations → Build → Polish</strong> — that ramps from learning each section's
          method to timed drills, full mocks and a final-week taper. Starts next Monday and replaces any earlier suggested plan.
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>Intensity</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {intensities.map((it) => (
              <button key={it.v} onClick={() => setIntensity(it.v)} style={{
                flex: 1, textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                border: `1.5px solid ${intensity === it.v ? 'var(--brand-purple)' : 'var(--border)'}`,
                background: intensity === it.v ? 'var(--accent-soft)' : '#fff',
                fontFamily: 'inherit',
              }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>{it.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{it.note}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>When's your UCAT? <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — builds the plan right up to exam day)</span></label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="date" value={examDate} min={minDate}
              onChange={(e) => setExamDate(e.target.value)}
              style={{ flex: 1, padding: '10px 12px', fontSize: 14, border: '1px solid var(--border-strong)', borderRadius: 10, fontFamily: 'inherit' }}
            />
            <button
              onClick={() => examValid && onApply({ weeks: weeksUntilExam, examDate: new Date(examDate), intensity })}
              disabled={!examValid}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap', opacity: examValid ? 1 : 0.5 }}
            >
              Build {examValid ? `${weeksUntilExam}-week ` : ''}plan
            </button>
          </div>
          {examDate && !examValid && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#b45309' }}>Pick a date at least 2 weeks away.</p>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 12px', color: 'var(--text-muted)', fontSize: 12 }}>
          <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />or pick a length<span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {[4, 6, 8, 10, 12].map((w) => (
            <button key={w} onClick={() => onApply({ weeks: w, examDate: null, intensity })} className="btn btn-secondary">{w} weeks</button>
          ))}
        </div>

        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginTop: 14 }}>Cancel</button>
      </div>
    </div>
  );
}

function buildIcs(events) {
  const pad = (n) => String(n).padStart(2, '0');
  const fmtDate = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const dtstamp = () => {
    const d = new Date();
    return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
  };
  const esc = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Medi (Medic Mind)//Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:Medi · UCAT prep',
    'X-WR-CALDESC:UCAS deadlines, Career Camp dates and your UCAT prep plan from medicmind.co.uk'
  ];
  for (const e of events) {
    const date = new Date(e.date);
    if (Number.isNaN(date.getTime())) continue;
    const next = new Date(date); next.setDate(date.getDate() + 1);
    const uid = `${e.id || (e.label || 'evt') + '-' + e.date}@medi.medicmind.co.uk`;
    lines.push(
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtstamp()}`,
      `DTSTART;VALUE=DATE:${fmtDate(date)}`,
      `DTEND;VALUE=DATE:${fmtDate(next)}`,
      `SUMMARY:${esc(e.label)}`,
      `CATEGORIES:${esc(e.kind || 'medi')}`,
      'END:VEVENT'
    );
  }
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

function buildMonthGrid(monthStart) {
  const grid = [];
  const first = new Date(monthStart);
  first.setDate(1);
  const startOffset = (first.getDay() + 6) % 7;
  first.setDate(1 - startOffset);
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() + i);
    grid.push({ date: d });
  }
  return grid;
}

function addMonths(d, n) {
  const next = new Date(d);
  next.setMonth(next.getMonth() + n);
  return next;
}

const navBtn = {
  width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
  background: '#fff', cursor: 'pointer', fontSize: 18, fontWeight: 600, color: 'var(--text-2)'
};
const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(35, 20, 63, 0.55)', backdropFilter: 'blur(4px)',
  display: 'grid', placeItems: 'center', zIndex: 100, padding: 16
};
const modalCard = {
  background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.25)'
};
